import React, {
  useEffect,
  useRef,
  useState,
} from 'react';

import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import {
  User,
  Settings,
  Calendar,
  FileText,
  LogOut,
  ChevronDown,
  Mail,
  Shield,
  LayoutDashboard,
  Trash2,
  AlertTriangle,
  Loader2,
} from 'lucide-react';

import {
  logout,
} from '../store/slices/authSlice';

import {
  clientAuthApi,
} from '../services/clientAuthApi';

import {
  useFrontoffWorkingMode,
} from '../hooks/useSystemStatus';

import {
  ModalHeader,
} from './ModalHeader';

interface UserMenuProps {
  user: {
    id: string;
    email: string;
    firstName: string;
    lastName: string;
    emailVerified: boolean;
  };
}

type DeleteStep =
  | 'warning'
  | 'reason'
  | 'confirm';

export function UserMenu({
  user,
}: UserMenuProps) {
  const {
    t,
  } = useTranslation();

  const dispatch =
    useDispatch();

  const navigate =
    useNavigate();

  /*
   * =========================================================
   * MODE FRONT-OFFICE
   * =========================================================
   */

  const {
    isLimited,
    loading: workingModeLoading,
  } = useFrontoffWorkingMode();

  /*
   * =========================================================
   * MENU STATE
   * =========================================================
   */

  const [
    isOpen,
    setIsOpen,
  ] = useState(false);

  const menuRef =
    useRef<HTMLDivElement>(
      null
    );

  /*
   * =========================================================
   * DELETE ACCOUNT MODAL
   * =========================================================
   */

  const [
    showDeleteModal,
    setShowDeleteModal,
  ] = useState(false);

  const [
    deleteStep,
    setDeleteStep,
  ] =
    useState<DeleteStep>(
      'warning'
    );

  const [
    deleteReason,
    setDeleteReason,
  ] = useState('');

  const [
    confirmText,
    setConfirmText,
  ] = useState('');

  const [
    password,
    setPassword,
  ] = useState('');

  const [
    deleting,
    setDeleting,
  ] = useState(false);

  const [
    deleteError,
    setDeleteError,
  ] = useState('');

  const deleteReasons = [
    'notUseful',
    'tooExpensive',
    'foundAlternative',
    'privacyConcerns',
    'technicalIssues',
    'other',
  ];

  /*
   * =========================================================
   * CLOSE DROPDOWN ON OUTSIDE CLICK
   * =========================================================
   */

  useEffect(() => {
    const handleClickOutside = (
      event: MouseEvent
    ) => {
      /*
       * Ne pas fermer le dropdown à cause
       * d'un clic dans la modale.
       */
      if (showDeleteModal) {
        return;
      }

      if (
        menuRef.current &&
        !menuRef.current.contains(
          event.target as Node
        )
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener(
      'mousedown',
      handleClickOutside
    );

    return () => {
      document.removeEventListener(
        'mousedown',
        handleClickOutside
      );
    };
  }, [
    showDeleteModal,
  ]);

  /*
   * =========================================================
   * DELETE MODAL — ESC + BODY SCROLL
   * =========================================================
   */

  useEffect(() => {
    if (!showDeleteModal) {
      return;
    }

    const previousOverflow =
      document.body.style.overflow;

    const handleKeyDown = (
      event: KeyboardEvent
    ) => {
      if (
        event.key ===
          'Escape' &&
        !deleting
      ) {
        closeDeleteModal();
      }
    };

    document.body.style.overflow =
      'hidden';

    document.addEventListener(
      'keydown',
      handleKeyDown
    );

    return () => {
      document.body.style.overflow =
        previousOverflow;

      document.removeEventListener(
        'keydown',
        handleKeyDown
      );
    };
  }, [
    showDeleteModal,
    deleting,
  ]);

  /*
   * =========================================================
   * LOGOUT
   * =========================================================
   */

  const handleLogout =
    async () => {
      try {
        const refreshToken =
          localStorage.getItem(
            'client_refresh_token'
          );

        if (refreshToken) {
          await clientAuthApi.logout(
            refreshToken
          );
        }
      } catch (error) {
        console.error(
          'Logout error:',
          error
        );
      } finally {
        dispatch(
          logout()
        );

        setIsOpen(
          false
        );

        navigate(
          '/'
        );
      }
    };

  /*
   * =========================================================
   * OPEN DELETE MODAL
   * =========================================================
   */

  const openDeleteModal =
    () => {
      setIsOpen(
        false
      );

      setDeleteStep(
        'warning'
      );

      setDeleteReason(
        ''
      );

      setConfirmText(
        ''
      );

      setPassword(
        ''
      );

      setDeleteError(
        ''
      );

      setShowDeleteModal(
        true
      );
    };

  /*
   * =========================================================
   * CLOSE DELETE MODAL
   * =========================================================
   */

  const closeDeleteModal =
    () => {
      if (deleting) {
        return;
      }

      setShowDeleteModal(
        false
      );

      setDeleteStep(
        'warning'
      );

      setDeleteReason(
        ''
      );

      setConfirmText(
        ''
      );

      setPassword(
        ''
      );

      setDeleteError(
        ''
      );
    };

  /*
   * =========================================================
   * DELETE ACCOUNT
   * =========================================================
   */

  const handleDeleteAccount =
    async () => {
      setDeleteError(
        ''
      );

      /*
       * Validation finale.
       */
      if (
        confirmText !==
        'DELETE'
      ) {
        setDeleteError(
          t(
            'auth.account.deleteAccount.modal.errors.confirmTextMismatch'
          )
        );

        return;
      }

      if (!password) {
        setDeleteError(
          t(
            'auth.account.deleteAccount.modal.errors.passwordRequired'
          )
        );

        return;
      }

      setDeleting(
        true
      );

      try {
        const token =
          localStorage.getItem(
            'client_token'
          );

        if (!token) {
          setDeleteError(
            t(
              'auth.account.deleteAccount.modal.errors.authRequired'
            )
          );

          return;
        }

        await clientAuthApi.deleteAccount(
          token,
          {
            password,
            reason:
              deleteReason ||
              undefined,
          }
        );

        dispatch(
          logout()
        );

        setShowDeleteModal(
          false
        );

        navigate(
          '/',
          {
            state: {
              message:
                t(
                  'auth.account.deleteAccount.success.message'
                ),
            },
          }
        );
      } catch (error) {
        console.error(
          'Failed to delete account:',
          error
        );

        const code = (
          error as Error & {
            code?: string;
          }
        ).code;

        setDeleteError(
          code ===
            'INVALID_PASSWORD'
            ? t(
                'auth.account.deleteAccount.modal.errors.passwordIncorrect'
              )
            : t(
                'auth.account.deleteAccount.modal.errors.deleteFailed'
              )
        );
      } finally {
        setDeleting(
          false
        );
      }
    };

  /*
   * =========================================================
   * FULL MODE MENU
   * =========================================================
   */

  const fullModeMenuItems = [
    {
      icon:
        LayoutDashboard,

      label:
        t(
          'auth.account.sidebar.dashboard'
        ),

      href:
        '/account',

      onClick:
        () => {
          navigate(
            '/account'
          );

          setIsOpen(
            false
          );
        },
    },

    {
      icon:
        User,

      label:
        t(
          'auth.account.sidebar.profile'
        ),

      href:
        '/account/profile',

      onClick:
        () => {
          navigate(
            '/account/profile'
          );

          setIsOpen(
            false
          );
        },
    },

    {
      icon:
        Calendar,

      label:
        t(
          'auth.account.sidebar.appointments'
        ),

      href:
        '/account/appointments',

      onClick:
        () => {
          navigate(
            '/account/appointments'
          );

          setIsOpen(
            false
          );
        },
    },

    {
      icon:
        FileText,

      label:
        t(
          'auth.account.sidebar.documents'
        ),

      href:
        '/account/documents',

      onClick:
        () => {
          navigate(
            '/account/documents'
          );

          setIsOpen(
            false
          );
        },
    },

    {
      icon:
        Settings,

      label:
        t(
          'auth.account.sidebar.preferences'
        ),

      href:
        '/account/preferences',

      onClick:
        () => {
          navigate(
            '/account/preferences'
          );

          setIsOpen(
            false
          );
        },
    },
  ];

  /*
   * =========================================================
   * LIMITED MODE MENU
   * =========================================================
   */

  const limitedModeMenuItems = [
    {
      icon:
        Trash2,

      label:
        t(
          'auth.account.security.dangerZone.deleteAccount',
          'Supprimer mon compte'
        ),

      href:
        '#delete-account',

      danger:
        true,

      onClick:
        openDeleteModal,
    },
  ];

  const menuItems =
    isLimited
      ? limitedModeMenuItems
      : fullModeMenuItems;

  /*
   * =========================================================
   * RENDER
   * =========================================================
   */

  return (
    <>
      <div
        className="relative"
        ref={menuRef}
      >
        {/* USER BUTTON */}

        <button
          type="button"
          onClick={() =>
            setIsOpen(
              (
                previous
              ) =>
                !previous
            )
          }
          className="flex items-center space-x-3 p-2 rounded-lg hover:bg-gray-100 transition-colors duration-200"
        >
          <div className="flex items-center space-x-2">
            <div className="w-8 h-8 bg-gradient-to-br from-primary-300 to-accent-500 rounded-full flex items-center justify-center">
              <span className="text-white text-sm font-medium">
                {user.firstName
                  .charAt(0)
                  .toUpperCase()}
              </span>
            </div>

            <div className="hidden sm:block text-left">
              <p className="text-sm font-medium text-textPrimary">
                {
                  user.firstName
                }{' '}
                {
                  user.lastName
                }
              </p>

              <div className="flex items-center space-x-1">
                {user.emailVerified ? (
                  <Shield className="w-3 h-3 text-green-500" />
                ) : (
                  <Mail className="w-3 h-3 text-yellow-500" />
                )}

                <p className="text-xs text-textSecondary">
                  {user.emailVerified
                    ? t(
                        'auth.userMenu.verified',
                        'Verified'
                      )
                    : t(
                        'auth.userMenu.unverified',
                        'Unverified'
                      )}
                </p>
              </div>
            </div>
          </div>

          <ChevronDown
            className={`w-4 h-4 text-textSecondary transition-transform duration-200 ${
              isOpen
                ? 'rotate-180'
                : ''
            }`}
          />
        </button>

        {/* DROPDOWN */}

        {isOpen && (
          <div className="absolute right-0 mt-2 w-64 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50">
            {/* USER HEADER */}

            <div className="px-4 py-3 border-b border-gray-100">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-gradient-to-br from-primary-300 to-accent-500 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-white font-medium">
                    {user.firstName
                      .charAt(0)
                      .toUpperCase()}
                  </span>
                </div>

                <div className="min-w-0">
                  <p className="text-sm font-medium text-textPrimary truncate">
                    {
                      user.firstName
                    }{' '}
                    {
                      user.lastName
                    }
                  </p>

                  <p className="text-xs text-textSecondary truncate">
                    {
                      user.email
                    }
                  </p>

                  {!user.emailVerified && (
                    <div className="flex items-center space-x-1 mt-1">
                      <Mail className="w-3 h-3 text-yellow-500 flex-shrink-0" />

                      <span className="text-xs text-yellow-600">
                        {t(
                          'auth.userMenu.emailNotVerified',
                          'Email not verified'
                        )}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* WORKING MODE LOADING */}

            {workingModeLoading ? (
              <div className="px-4 py-5 flex justify-center">
                <Loader2 className="w-5 h-5 animate-spin text-primary-500" />
              </div>
            ) : (
              <>
                {/* MENU ITEMS */}

                <div className="py-2">
                  {menuItems.map(
                    (
                      item
                    ) => {
                      const ItemIcon =
                        item.icon;

                      const isDanger =
                        'danger' in
                          item &&
                        item.danger ===
                          true;

                      return (
                        <button
                          key={
                            item.href
                          }
                          type="button"
                          onClick={
                            item.onClick
                          }
                          className={`w-full flex items-center space-x-3 px-4 py-2 text-sm transition-colors duration-200 ${
                            isDanger
                              ? 'text-red-600 hover:bg-red-50 hover:text-red-700'
                              : 'text-textSecondary hover:bg-gray-50 hover:text-textPrimary'
                          }`}
                        >
                          <ItemIcon className="w-4 h-4" />

                          <span>
                            {
                              item.label
                            }
                          </span>
                        </button>
                      );
                    }
                  )}
                </div>

                {/* LOGOUT */}

                <div className="border-t border-gray-100 pt-2">
                  <button
                    type="button"
                    onClick={
                      handleLogout
                    }
                    className="w-full flex items-center space-x-3 px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors duration-200"
                  >
                    <LogOut className="w-4 h-4" />

                    <span>
                      {t(
                        'auth.account.sidebar.logout'
                      )}
                    </span>
                  </button>
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {/*
       * =====================================================
       * DELETE ACCOUNT MODAL
       * =====================================================
       */}

      {showDeleteModal && createPortal(
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm"
          role="dialog"
          aria-modal="true"
          onMouseDown={() => {
            if (
              !deleting
            ) {
              closeDeleteModal();
            }
          }}
        >
          <div
            className="modal-panel w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-xl bg-white shadow-2xl"
            onMouseDown={(
              event
            ) =>
              event.stopPropagation()
            }
          >
            <div className="p-6">
              {/* HEADER */}

              <ModalHeader
                title={
                  <div className="flex items-center space-x-3 pr-2">
                    <div className="flex items-center justify-center w-12 h-12 bg-red-100 rounded-full shrink-0">
                      <AlertTriangle className="w-6 h-6 text-red-600" />
                    </div>

                    <h3 className="text-2xl font-bold text-textPrimary">
                      {deleteStep ===
                        'warning' &&
                        t(
                          'auth.account.deleteAccount.modal.warningTitle'
                        )}

                      {deleteStep ===
                        'reason' &&
                        t(
                          'auth.account.deleteAccount.modal.reasonTitle'
                        )}

                      {deleteStep ===
                        'confirm' &&
                        t(
                          'auth.account.deleteAccount.modal.confirmTitle'
                        )}
                    </h3>
                  </div>
                }
                onClose={
                  closeDeleteModal
                }
                disabled={
                  deleting
                }
                className="mb-6"
              />

              {/* ERROR */}

              {deleteError && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center space-x-2">
                  <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0" />

                  <p className="text-sm text-red-800">
                    {
                      deleteError
                    }
                  </p>
                </div>
              )}

              {/*
               * ===============================================
               * STEP 1 — WARNING
               * ===============================================
               */}

              {deleteStep ===
                'warning' && (
                <div className="space-y-4">
                  <p className="text-textSecondary">
                    {t(
                      'auth.account.deleteAccount.modal.warningDescription'
                    )}
                  </p>

                  <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <p className="text-sm font-medium text-red-900 mb-2">
                      {t(
                        'auth.account.deleteAccount.modal.warningList'
                      )}
                    </p>

                    <ul className="space-y-1 text-sm text-red-800">
                      <li>
                        •{' '}
                        {t(
                          'auth.account.deleteAccount.modal.warning1'
                        )}
                      </li>

                      <li>
                        •{' '}
                        {t(
                          'auth.account.deleteAccount.modal.warning2'
                        )}
                      </li>

                      <li>
                        •{' '}
                        {t(
                          'auth.account.deleteAccount.modal.warning3'
                        )}
                      </li>
                    </ul>
                  </div>

                  <div className="flex flex-col-reverse sm:flex-row gap-3">
                    <button
                      type="button"
                      onClick={
                        closeDeleteModal
                      }
                      className="btn-secondary flex-1"
                    >
                      {t(
                        'common.cancel'
                      )}
                    </button>

                    <button
                      type="button"
                      onClick={() => {
                        setDeleteError(
                          ''
                        );

                        setDeleteStep(
                          'reason'
                        );
                      }}
                      className="bg-red-600 hover:bg-red-700 text-white px-6 py-3 rounded-lg font-medium transition-colors flex-1"
                    >
                      {t(
                        'auth.account.deleteAccount.modal.continue'
                      )}
                    </button>
                  </div>
                </div>
              )}

              {/*
               * ===============================================
               * STEP 2 — REASON
               * ===============================================
               */}

              {deleteStep ===
                'reason' && (
                <div className="space-y-4">
                  <p className="text-textSecondary">
                    {t(
                      'auth.account.deleteAccount.modal.reasonDescription'
                    )}
                  </p>

                  <div className="space-y-2">
                    {deleteReasons.map(
                      (
                        reasonKey
                      ) => (
                        <label
                          key={
                            reasonKey
                          }
                          className="flex items-center p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors"
                        >
                          <input
                            type="radio"
                            name="delete-reason"
                            value={
                              reasonKey
                            }
                            checked={
                              deleteReason ===
                              reasonKey
                            }
                            onChange={(
                              event
                            ) => {
                              setDeleteReason(
                                event.target
                                  .value
                              );

                              setDeleteError(
                                ''
                              );
                            }}
                            className="mr-3"
                          />

                          <span className="text-sm text-textPrimary">
                            {t(
                              `auth.account.deleteAccount.reasons.${reasonKey}`
                            )}
                          </span>
                        </label>
                      )
                    )}
                  </div>

                  <div className="flex flex-col sm:flex-row gap-3">
                    <button
                      type="button"
                      onClick={() =>
                        setDeleteStep(
                          'warning'
                        )
                      }
                      className="btn-secondary flex-1"
                    >
                      {t(
                        'common.previous'
                      )}
                    </button>

                    <button
                      type="button"
                      onClick={
                        closeDeleteModal
                      }
                      className="btn-secondary flex-1"
                    >
                      {t(
                        'common.cancel'
                      )}
                    </button>

                    <button
                      type="button"
                      onClick={() => {
                        setDeleteError(
                          ''
                        );

                        setDeleteStep(
                          'confirm'
                        );
                      }}
                      disabled={
                        !deleteReason
                      }
                      className="bg-red-600 hover:bg-red-700 text-white px-6 py-3 rounded-lg font-medium transition-colors flex-1 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {t(
                        'auth.account.deleteAccount.modal.continue'
                      )}
                    </button>
                  </div>
                </div>
              )}

              {/*
               * ===============================================
               * STEP 3 — FINAL CONFIRMATION
               * ===============================================
               */}

              {deleteStep ===
                'confirm' && (
                <div className="space-y-4">
                  <p className="text-textSecondary mb-4">
                    {t(
                      'auth.account.deleteAccount.modal.confirmDescription'
                    )}
                  </p>

                  {/* DELETE TEXT */}

                  <div>
                    <label className="block text-sm font-medium text-textPrimary mb-2">
                      {t(
                        'auth.account.deleteAccount.modal.typeDelete'
                      )}
                    </label>

                    <input
                      type="text"
                      value={
                        confirmText
                      }
                      onChange={(
                        event
                      ) => {
                        setConfirmText(
                          event.target.value
                        );

                        setDeleteError(
                          ''
                        );
                      }}
                      placeholder="DELETE"
                      autoComplete="off"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    />

                    <p className="mt-1 text-xs text-textSecondary">
                      {t(
                        'auth.account.deleteAccount.modal.typeDeleteHelp'
                      )}
                    </p>
                  </div>

                  {/* PASSWORD */}

                  <div>
                    <label className="block text-sm font-medium text-textPrimary mb-2">
                      {t(
                        'auth.account.deleteAccount.modal.confirmPassword'
                      )}
                    </label>

                    <input
                      type="password"
                      value={
                        password
                      }
                      onChange={(
                        event
                      ) => {
                        setPassword(
                          event.target.value
                        );

                        setDeleteError(
                          ''
                        );
                      }}
                      placeholder={
                        t(
                          'auth.account.deleteAccount.modal.passwordPlaceholder'
                        )
                      }
                      autoComplete="current-password"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    />
                  </div>

                  {/* FINAL WARNING */}

                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                    <p className="text-sm text-yellow-900">
                      <strong>
                        {t(
                          'auth.account.deleteAccount.modal.finalWarning'
                        )}
                      </strong>
                    </p>
                  </div>

                  {/* ACTIONS */}

                  <div className="flex flex-col sm:flex-row gap-3">
                    <button
                      type="button"
                      onClick={() => {
                        setDeleteError(
                          ''
                        );

                        setDeleteStep(
                          'reason'
                        );
                      }}
                      disabled={
                        deleting
                      }
                      className="btn-secondary flex-1"
                    >
                      {t(
                        'common.previous'
                      )}
                    </button>

                    <button
                      type="button"
                      onClick={
                        closeDeleteModal
                      }
                      disabled={
                        deleting
                      }
                      className="btn-secondary flex-1"
                    >
                      {t(
                        'common.cancel'
                      )}
                    </button>

                    <button
                      type="button"
                      onClick={
                        handleDeleteAccount
                      }
                      disabled={
                        deleting ||
                        confirmText !==
                          'DELETE' ||
                        !password
                      }
                      className="bg-red-600 hover:bg-red-700 text-white px-6 py-3 rounded-lg font-medium transition-colors flex items-center justify-center space-x-2 flex-1 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {deleting ? (
                        <>
                          <Loader2 className="w-5 h-5 animate-spin" />

                          <span>
                            {t(
                              'auth.account.deleteAccount.modal.deleting'
                            )}
                          </span>
                        </>
                      ) : (
                        <>
                          <Trash2 className="w-5 h-5" />

                          <span>
                            {t(
                              'auth.account.deleteAccount.modal.deleteButton'
                            )}
                          </span>
                        </>
                      )}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
}

export default UserMenu;